/**
 * User Context — Profile and business logic layer
 * 
 * IMPORTANT: This context READS auth state from AuthContext.
 * It does NOT call supabase.auth.getSession() or supabase.auth.getUser()!
 * 
 * Auth state flows:
 *   AuthContext (singleton) → UserContext (profile enrichment) → Components
 * 
 * This separation ensures:
 * - Auth API calls happen exactly once (in AuthContext)
 * - UserContext focuses on profile data from users table
 * - No duplicate auth requests that caused quota exhaustion
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { logger, setUserLogContext } from '@/lib/services';
import { storageService } from '@/lib/services/storageService';
import { startPushWorker, stopPushWorker } from '@/lib/services/pushNotificationWorker';
import { cacheConfig, getCachedData, setCacheData, invalidateCache } from '@/lib/config/cacheConfig';
import type { Id } from '@/lib/types';

// Defensive check - if React isn't properly loaded, fail fast with clear message
if (!React || typeof React.createContext !== 'function') {
  const err = new Error('[UserContext] React is not available or createContext is missing');
  console.error(err);
  throw err;
}

export interface UserProfile {
  id: Id;
  email?: string;
  displayName?: string;
  fullName?: string;
  avatarUrl?: string | null;
  phone?: string | null;
  bio?: string | null;
  socialUrl?: string | null;
  telegramUsername?: string | null;
  emailConfirmed?: boolean;
  surveyCompleted?: boolean;
  isActive?: boolean;
  locale?: string;
  timezone?: string;
  geoPlaceId?: string | null;
  cityName?: string | null;
}

export interface QuizProfile {
  speciesKey: string | null;
  breedIds: string[];
  desiredSize: string | null;
}

export interface UserContextValue {
  user: UserProfile | null;
  quizProfile: QuizProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authReady: boolean; // True when initial auth check is complete (for routing)
  login: (email: string, password: string) => Promise<{ error: Error | null }>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshQuizProfile: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error: Error | null }>;
  resendConfirmationEmail: () => Promise<{ error: Error | null }>;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

/**
 * Fetch user profile using optimized RPC that JOINs users, quiz_profiles, and geo_cities
 * in a single database round-trip instead of 2-3 separate queries.
 * Uses localStorage cache for faster subsequent loads.
 */
async function fetchUserProfile(userId: string, emailConfirmedAt?: string | null, forceRefresh = false): Promise<UserProfile | null> {
  const { ttlMs, storageKey } = cacheConfig.userProfile;
  
  // Try cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = getCachedData<UserProfile & { _cachedUserId: string }>(storageKey, ttlMs);
    if (cached && cached._cachedUserId === userId) {
      // Return cached profile (without the internal _cachedUserId field)
      const { _cachedUserId, ...profile } = cached;
      return profile;
    }
  }

  // Single RPC call with all data joined server-side
  const { data, error } = await supabase.rpc('me_profile_full');

  if (error) {
    logger.error('Failed to fetch user profile via RPC', {
      context: { function_name: 'fetchUserProfile', feature: 'auth' },
      error,
      error_code: 'auth_profile_fetch_failed',
      error_type: 'network',
    });
    return null;
  }

  if (!data) {
    return null;
  }

  // Type the RPC response
  const profileData = data as {
    id: string;
    email: string | null;
    display_name: string | null;
    full_name: string | null;
    avatar_url: string | null;
    phone: string | null;
    bio: string | null;
    locale: string | null;
    timezone: string | null;
    is_active: boolean;
    geo_place_id: string | null;
    city_name: string | null;
    survey_completed: boolean;
  };

  // Add cache-busting to avatar URL to prevent stale images
  let avatarUrl = profileData.avatar_url;
  if (avatarUrl) {
    const separator = avatarUrl.includes('?') ? '&' : '?';
    avatarUrl = `${avatarUrl}${separator}v=${Date.now()}`;
  }

  const profile: UserProfile = {
    id: profileData.id,
    email: profileData.email ?? undefined,
    displayName: profileData.display_name ?? undefined,
    fullName: profileData.full_name ?? undefined,
    avatarUrl,
    phone: profileData.phone,
    bio: profileData.bio,
    isActive: profileData.is_active,
    locale: profileData.locale ?? undefined,
    timezone: profileData.timezone ?? undefined,
    emailConfirmed: !!emailConfirmedAt,
    surveyCompleted: profileData.survey_completed ?? false,
    geoPlaceId: profileData.geo_place_id,
    cityName: profileData.city_name,
  };

  // Cache the profile with userId marker for validation
  setCacheData(storageKey, { ...profile, _cachedUserId: userId });

  return profile;
}

export function UserProvider({ children }: UserProviderProps) {
  // Read auth state from AuthContext (singleton)
  const { user: authUser, session, authStatus, authReady: authContextReady } = useAuthContext();
  
  const [user, setUser] = useState<UserProfile | null>(null);
  const [quizProfile, setQuizProfile] = useState<QuizProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Fetch quiz profile with caching
  const fetchQuizProfile = useCallback(async (userId: string, forceRefresh = false): Promise<QuizProfile | null> => {
    const { ttlMs, storageKey } = cacheConfig.quizProfile;
    
    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = getCachedData<QuizProfile & { _cachedUserId: string }>(storageKey, ttlMs);
      if (cached && cached._cachedUserId === userId) {
        const { _cachedUserId, ...profile } = cached;
        return profile;
      }
    }
    
    try {
      const { data, error } = await supabase
        .from('quiz_profiles')
        .select('species_key, specific_breed_id, desired_size')
        .eq('user_id', userId)
        .eq('is_completed', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        logger.warn('Failed to fetch quiz profile', { error });
        return null;
      }

      if (!data) {
        return null;
      }

      const profile: QuizProfile = {
        speciesKey: data.species_key || null,
        breedIds: data.specific_breed_id ? [data.specific_breed_id] : [],
        desiredSize: data.desired_size || null,
      };

      // Cache with userId marker
      setCacheData(storageKey, { ...profile, _cachedUserId: userId });
      return profile;
    } catch (e) {
      logger.warn('Error fetching quiz profile', { error: e });
      return null;
    }
  }, []);

  // Fetch profile and update state
  const loadProfile = useCallback(async (userId: string, email?: string, emailConfirmedAt?: string | null) => {
    // Set current user ID for user-specific localStorage
    storageService.setCurrentUserId(userId);
    
    // Load user profile and quiz profile in parallel
    const [profile, quiz] = await Promise.all([
      fetchUserProfile(userId, emailConfirmedAt),
      fetchQuizProfile(userId),
    ]);

    if (profile) {
      setUser(profile);
    } else {
      // User not in users table yet (new signup)
      setUser({
        id: userId,
        email,
        emailConfirmed: !!emailConfirmedAt,
      });
    }
    
    setQuizProfile(quiz);
    setIsLoading(false);
    setProfileLoaded(true);
    
    // Start push notification worker for curators/admins
    startPushWorker();
  }, [fetchQuizProfile]);

  // React to auth state changes from AuthContext
  useEffect(() => {
    // Wait for AuthContext to be ready (also handles HMR gracefully)
    // Check both authContextReady and authStatus to ensure full initialization
    if (!authContextReady || authStatus === 'loading') return;

    if (authUser && session) {
      // User is authenticated — load profile
      setIsLoading(true);
      loadProfile(
        authUser.id,
        authUser.email ?? undefined,
        authUser.email_confirmed_at
      );
    } else {
      // User is not authenticated
      setUser(null);
      setIsLoading(false);
      setProfileLoaded(true);
    }
  }, [authUser?.id, authContextReady, session, loadProfile]);

  // Derived auth ready state
  const authReady = authContextReady && profileLoaded;

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        return { error };
      }
      return { error: null };
    } catch (e) {
      return { error: e instanceof Error ? e : new Error('Login failed') };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      // Stop push notification worker
      stopPushWorker();
      
      // Clear user-specific storage data
      storageService.clearUserData();
      
      // Clear quiz and AI profile caches
      invalidateCache(cacheConfig.quizProfile.storageKey);
      invalidateCache(cacheConfig.aiFilterProfile.storageKey);
      
      await supabase.auth.signOut();
      setUser(null);
      setQuizProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      // Invalidate cache and force refresh from server
      invalidateCache(cacheConfig.userProfile.storageKey);
      const profile = await fetchUserProfile(user.id, session?.user?.email_confirmed_at, true);
      if (profile) {
        setUser(profile);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, session]);

  const refreshQuizProfile = useCallback(async () => {
    if (!user?.id) return;
    // Invalidate cache and force refresh
    invalidateCache(cacheConfig.quizProfile.storageKey);
    const quiz = await fetchQuizProfile(user.id as string, true);
    setQuizProfile(quiz);
  }, [user?.id, fetchQuizProfile]);

  const updateProfile = useCallback(async (updates: Partial<UserProfile>) => {
    if (!user?.id) {
      return { error: new Error('No user logged in') };
    }

    // Map UserProfile fields to database columns
    const dbUpdates: {
      display_name?: string;
      full_name?: string;
      avatar_url?: string | null;
      phone?: string | null;
      geo_place_id?: string | null;
      bio?: string | null;
    } = {};
    if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName;
    if (updates.fullName !== undefined) dbUpdates.full_name = updates.fullName;
    if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl;
    if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
    if (updates.geoPlaceId !== undefined) dbUpdates.geo_place_id = updates.geoPlaceId;
    if (updates.bio !== undefined) dbUpdates.bio = updates.bio;

    const { error } = await supabase
      .from('users')
      .update(dbUpdates)
      .eq('id', user.id as string);

    if (error) {
      return { error };
    }

    // Refresh profile after update
    await refreshProfile();
    return { error: null };
  }, [user?.id, refreshProfile]);

  const resendConfirmationEmail = useCallback(async () => {
    if (!user?.email) {
      return { error: new Error('No email address') };
    }

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
      });
      if (error) {
        return { error };
      }
      return { error: null };
    } catch (e) {
      return { error: e instanceof Error ? e : new Error('Failed to resend email') };
    }
  }, [user?.email]);

  useEffect(() => {
    setUserLogContext(user ? { id: user.id, role: user.isActive ? 'authenticated' : 'guest' } : undefined);
  }, [user]);

  const value: UserContextValue = {
    user,
    quizProfile,
    isAuthenticated: !!user,
    isLoading,
    authReady,
    login,
    logout,
    refreshProfile,
    refreshQuizProfile,
    updateProfile,
    resendConfirmationEmail,
  };

  // In WebView: render children immediately, don't wait for auth
  // In browser: could optionally show loading, but we render immediately too for consistency
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUserContext(): UserContextValue {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
}

export default UserContext;
